import { CommunityAccessType, CommunityMembershipRole } from '../enums';

/** Grace period for past_due Circle memberships (D-OPEN-01). */
export const COMMUNITY_MEMBERSHIP_GRACE_DAYS = 3;

export type CommunityMembershipBillingFields = {
  status: string;
  role?: string | null;
  stripeBillingStatus?: string | null;
  stripeSubscriptionId?: string | null;
  graceEndsAt?: Date | string | null;
  cancelAtPeriodEnd?: boolean | null;
  currentPeriodEnd?: Date | string | null;
  subscriptionPausedAt?: Date | string | null;
  subscriptionPauseResumesAt?: Date | string | null;
};

export function getCommercePlatformFeePercent(): number {
  const raw = process.env.COMMERCE_PLATFORM_FEE_PERCENT?.trim();
  if (!raw) return 10;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return 10;
  return parsed;
}

export function isCommerceDevStubEnabled(): boolean {
  const raw = process.env.COMMERCE_DEV_STUB;
  if (raw === 'false' || raw === '0') return false;
  if (raw === 'true' || raw === '1') return true;
  return !process.env.STRIPE_SECRET_KEY?.trim();
}

export function stripeStatusGrantsCommunityMembershipAccess(stripeStatus: string | null | undefined): boolean {
  return (
    stripeStatus === 'active' ||
    stripeStatus === 'trialing' ||
    stripeStatus === 'lifetime'
  );
}

function parseDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isCommunityMembershipSubscriptionPaused(
  membership: Pick<CommunityMembershipBillingFields, 'subscriptionPausedAt'>,
): boolean {
  return Boolean(parseDate(membership.subscriptionPausedAt));
}

export function canPauseCommunityMembershipSubscription(
  membership: CommunityMembershipBillingFields,
): { ok: true } | { ok: false; message: string } {
  if (!membership.stripeSubscriptionId?.trim()) {
    return { ok: false, message: 'No active subscription to pause' };
  }
  if (isCommunityMembershipSubscriptionPaused(membership)) {
    return { ok: false, message: 'Subscription is already paused' };
  }
  if (membership.cancelAtPeriodEnd) {
    return { ok: false, message: 'Resume or undo cancellation before pausing billing' };
  }
  const stripeStatus = membership.stripeBillingStatus?.trim() ?? null;
  if (stripeStatus !== 'active' && stripeStatus !== 'trialing') {
    return { ok: false, message: 'Only active subscriptions can be paused' };
  }
  return { ok: true };
}

export function canResumeCommunityMembershipSubscription(
  membership: Pick<CommunityMembershipBillingFields, 'subscriptionPausedAt'>,
): { ok: true } | { ok: false; message: string } {
  if (!isCommunityMembershipSubscriptionPaused(membership)) {
    return { ok: false, message: 'Subscription is not paused' };
  }
  return { ok: true };
}

/** Whether paid billing still grants member access (including grace and cancel-at-period-end). */
export function grantsPaidCommunityMembershipBillingAccess(
  membership: CommunityMembershipBillingFields,
  now: Date = new Date(),
): boolean {
  const stripeStatus = membership.stripeBillingStatus?.trim() ?? null;
  if (stripeStatusGrantsCommunityMembershipAccess(stripeStatus)) {
    return true;
  }

  if (stripeStatus === 'past_due') {
    const graceEndsAt = parseDate(membership.graceEndsAt);
    if (graceEndsAt && graceEndsAt.getTime() > now.getTime()) {
      return true;
    }
  }

  if (membership.cancelAtPeriodEnd) {
    const periodEnd = parseDate(membership.currentPeriodEnd);
    if (periodEnd && periodEnd.getTime() > now.getTime()) {
      return true;
    }
  }

  return false;
}

export function grantsCommunityMembershipEntitlement(
  membership: CommunityMembershipBillingFields | null | undefined,
  accessType: CommunityAccessType | string = CommunityAccessType.FREE,
  now: Date = new Date(),
): boolean {
  if (!membership || membership.status !== 'active') {
    return false;
  }
  if (membership.role === CommunityMembershipRole.OWNER) {
    return true;
  }
  if (accessType !== CommunityAccessType.PAID) {
    return true;
  }
  return grantsPaidCommunityMembershipBillingAccess(membership, now);
}

export function computeCommunityMembershipGraceEndsAt(
  pastDueAt: Date,
  graceDays: number = COMMUNITY_MEMBERSHIP_GRACE_DAYS,
): Date {
  return new Date(pastDueAt.getTime() + graceDays * 24 * 60 * 60 * 1000);
}

export function formatCommunityPrice(cents: number, currency = 'usd'): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

export type CommunityMrrMembership = {
  billingInterval?: string | null;
  stripeBillingStatus?: string | null;
  stripeSubscriptionId?: string | null;
  subscriptionPausedAt?: Date | string | null;
  monthlyPriceCents?: number | null;
  annualPriceCents?: number | null;
};

export type CommunityMrrPricing = {
  monthlyPriceCents?: number | null;
  annualPriceCents?: number | null;
};

/** Sum normalized monthly revenue from active paid memberships. */
export function calculateCommunityMrrCents(
  memberships: CommunityMrrMembership[],
  pricing: CommunityMrrPricing = {},
): number {
  let mrrCents = 0;
  for (const membership of memberships) {
    if (!membership.stripeSubscriptionId) continue;
    if (isCommunityMembershipSubscriptionPaused(membership)) continue;
    if (!stripeStatusGrantsCommunityMembershipAccess(membership.stripeBillingStatus)) {
      continue;
    }
    const monthly = membership.monthlyPriceCents ?? pricing.monthlyPriceCents ?? 0;
    const annual = membership.annualPriceCents ?? pricing.annualPriceCents ?? 0;
    if (membership.billingInterval === 'year') {
      mrrCents += Math.round(annual / 12);
    } else {
      mrrCents += monthly;
    }
  }
  return mrrCents;
}
